import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { Continent } from '@shared-models';
import { API_CONFIG, ApiConfig } from '../api-config';
import { ApiResponse } from '@shared-models';

interface PageableData {
  content: any[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  first: boolean;
  last: boolean;
  empty: boolean;
}

export interface ContinentHistoryAction {
  id: number;
  date: Date;
  action: string;
  user: string;
  ip: string;
  device: string;
  browser: string;
  location: string;
}

@Injectable({ providedIn: 'root' })
export class ContinentService {
  private readonly httpClient = inject(HttpClient);
  private readonly apiConfig = inject<ApiConfig>(API_CONFIG);

  private get apiUrl(): string {
    return `${this.apiConfig.apiUrl}/settings/continents`;
  }

  /**
   * Normaliza el status de mayúsculas a minúsculas (ACTIVE -> active)
   */
  private normalizeStatus(status: string | undefined): 'active' | 'inactive' {
    if (!status) return 'active';
    return status.toLowerCase() as 'active' | 'inactive';
  }

  /**
   * Transforma la respuesta del backend al modelo del frontend
   */
  private mapToContinent(item: any): Continent {
    return {
      id: item.id?.toString() || '',
      name: item.name || '',
      status: this.normalizeStatus(item.status),
    };
  }

  /**
   * Obtiene todos los continentes (con paginación del backend)
   * GET /api/settings/continents?page=0&size=20&sort=createdAt&direction=desc
   */
  getAll(
    page = 0,
    size = 20,
    sort = 'createdAt',
    direction: 'asc' | 'desc' = 'desc',
  ): Observable<Continent[]> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString())
      .set('sort', sort)
      .set('direction', direction);

    return this.httpClient
      .get<ApiResponse<PageableData>>(this.apiUrl, { params })
      .pipe(
        map((response) => {
          const pageableData = response.data;
          const items = pageableData?.content || [];
          return items.map((item: any) => this.mapToContinent(item));
        }),
      );
  }

  /**
   * Obtiene un continente por ID
   * GET /api/settings/continents/{id}
   */
  getById(id: string): Observable<Continent> {
    return this.httpClient
      .get<ApiResponse<any>>(`${this.apiUrl}/${id}`)
      .pipe(map((response) => this.mapToContinent(response.data)));
  }

  /**
   * Crea un nuevo continente
   * POST /api/settings/continents
   */
  create(data: Omit<Continent, 'id'>): Observable<{ message: string }> {
    return this.httpClient
      .post<{ success: boolean; message: string }>(this.apiUrl, data)
      .pipe(
        map((response) => ({
          message: response.message || 'Continente creado exitosamente',
        })),
      );
  }

  /**
   * Actualiza un continente existente
   * PUT /api/settings/continents/{id}
   */
  update(
    id: string,
    data: Partial<Continent>,
  ): Observable<{ message: string }> {
    return this.httpClient
      .put<{ success: boolean; message: string }>(`${this.apiUrl}/${id}`, data)
      .pipe(
        map((response) => ({
          message: response.message || 'Continente actualizado exitosamente',
        })),
      );
  }

  /**
   * Elimina un continente
   * DELETE /api/settings/continents/{id}
   */
  delete(id: string): Observable<{ message: string }> {
    return this.httpClient
      .delete<{ success: boolean; message: string }>(`${this.apiUrl}/${id}`)
      .pipe(
        map((response) => ({
          message: response.message || 'Continente eliminado exitosamente',
        })),
      );
  }

  /**
   * Obtiene el historial de acciones de un continente
   * GET /api/settings/continents/{id}/history
   */
  getHistory(
    id: string,
    page = 0,
    size = 20,
  ): Observable<ContinentHistoryAction[]> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString());

    return this.httpClient
      .get<ApiResponse<PageableData>>(`${this.apiUrl}/${id}/history`, {
        params,
      })
      .pipe(
        map((response) => {
          const items = response.data?.content || [];
          return items.map((action: any) => ({
            id: action.id,
            date: action.date,
            action: action.action || 'Acción',
            user: action.user || 'Usuario',
            ip: action.ip || '',
            device: action.device || '',
            browser: action.browser || '',
            location: action.location || '',
          }));
        }),
      );
  }

  /**
   * Actualiza el status de un continente
   * PATCH /api/settings/continents/{id}/status
   */
  updateStatus(
    id: string,
    status: 'active' | 'inactive',
  ): Observable<{ message: string }> {
    return this.httpClient
      .patch<{
        success: boolean;
        message: string;
      }>(`${this.apiUrl}/${id}/status`, { status })
      .pipe(
        map((response) => ({
          message: response.message || 'Status actualizado exitosamente',
        })),
      );
  }

  /**
   * Exporta la lista de continentes a PDF
   * La API genera y descarga el PDF directamente
   */
  exportPdf(options?: {
    title?: string;
    status?: string;
    search?: string;
    page?: number;
    size?: number;
  }): Observable<Blob> {
    const params = new HttpParams()
      .set('title', options?.title || 'Reporte de continentes')
      .set('status', options?.status || '')
      .set('search', options?.search || '')
      .set('page', (options?.page ?? 0).toString())
      .set('size', (options?.size ?? 1000).toString());

    return this.httpClient.get(
      `${this.apiConfig.apiUrl}/export/pdf/continent`,
      {
        params,
        responseType: 'blob',
      },
    );
  }

  /**
   * Exporta la lista de continentes a Excel
   * La API genera y descarga el archivo Excel directamente
   */
  exportExcel(options?: {
    title?: string;
    status?: string;
    search?: string;
    page?: number;
    size?: number;
  }): Observable<Blob> {
    const params = new HttpParams()
      .set('title', options?.title || 'Reporte de continentes')
      .set('status', options?.status || '')
      .set('search', options?.search || '')
      .set('page', (options?.page ?? 0).toString())
      .set('size', (options?.size ?? 1000).toString());

    return this.httpClient.get(
      `${this.apiConfig.apiUrl}/export/excel/continent`,
      {
        params,
        responseType: 'blob',
      },
    );
  }

  /**
   * Exporta el historial de un continente a PDF
   * GET /api/export/pdf/history/continent/{id}?title=...
   */
  exportHistoryPdf(
    id: string,
    options?: {
      title?: string;
    },
  ): Observable<Blob> {
    const params = new HttpParams().set(
      'title',
      options?.title || 'Historial continentes',
    );

    return this.httpClient.get(
      `${this.apiConfig.apiUrl}/export/pdf/history/continent/${id}`,
      {
        params,
        responseType: 'blob',
      },
    );
  }

  /**
   * Exporta el historial de un continente a Excel
   * GET /api/export/excel/history/continent/{id}?title=...
   */
  exportHistoryExcel(
    id: string,
    options?: {
      title?: string;
    },
  ): Observable<Blob> {
    const params = new HttpParams().set(
      'title',
      options?.title || 'Historial continentes',
    );

    return this.httpClient.get(
      `${this.apiConfig.apiUrl}/export/excel/history/continent/${id}`,
      {
        params,
        responseType: 'blob',
      },
    );
  }
}
